import { TicketDetailsModel, UpdateTicketPayload } from "@jugl-web/rest-api";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import { TableGridColumn } from "@jugl-web/ui-kit";
import { useMemo } from "react";
import { AssigneesCellContent } from "../components/AssigneesCellContent";
import { ContactCellContent } from "../components/ContactCellContent";
import { CreatedAtCellContent } from "../components/CreatedAtCellContent";
import { DueDateCellContent } from "../components/DueDateCellContent";
import { PriorityCellContent } from "../components/PriorityCellContent";
import { StatusCellContent } from "../components/StatusCellContent";
import { TeamNotesCellContent } from "../components/TeamNotesCellContent";
import { TicketNameCellContent } from "../components/TicketNameCellContent";

interface UseTicketsViewTableColumnsOptions {
  entityId: string;
  produceUpdateTicketFn: (
    ticketId: string
  ) => (data: UpdateTicketPayload) => Promise<TicketDetailsModel | undefined>;
}

export const useTicketsViewTableColumns = ({
  entityId,
  produceUpdateTicketFn,
}: UseTicketsViewTableColumnsOptions) => {
  const columns = useMemo<TableGridColumn<TicketListItemModel>[]>(
    () => [
      {
        id: "ticket-name",
        title: "Ticket name & ID",
        width: 368,
        resizeOptions: { isEnabled: true, min: 250 },
        className: "min-h-[79px] overflow-hidden lg:sticky! lg:z-10",
        headerClassName: "px-4 h-10 lg:sticky! lg:z-10",
        style: { left: 0 },
        headerStyle: { left: 0 },
        content: (ticket) => (
          <TicketNameCellContent
            ticket={ticket}
            onUpdate={produceUpdateTicketFn(ticket.id)}
          />
        ),
      },
      {
        id: "team-notes",
        title: "Team Notes",
        width: 110,
        resizeOptions: { isEnabled: true },
        className: "min-h-[79px]",
        headerClassName: "px-4 h-10",
        content: (ticket) => <TeamNotesCellContent ticket={ticket} />,
      },
      {
        id: "customer",
        title: "Customer",
        width: 180,
        resizeOptions: { isEnabled: true },
        className: "min-h-[79px]",
        headerClassName: "px-4 h-10",
        content: (ticket) => (
          <ContactCellContent
            ticket={ticket}
            onUpdate={produceUpdateTicketFn(ticket.id)}
          />
        ),
      },
      {
        id: "status",
        title: "Status",
        width: 180,
        resizeOptions: { isEnabled: true },
        className: "min-h-[79px]",
        headerClassName: "px-4 h-10",
        content: (ticket) => (
          <StatusCellContent
            ticket={ticket}
            onUpdate={produceUpdateTicketFn(ticket.id)}
          />
        ),
      },
      {
        id: "assignees",
        title: "Assignees",
        width: 180,
        resizeOptions: { isEnabled: true, min: 160 },
        className: "min-h-[79px]",
        headerClassName: "px-4 h-10",
        content: (ticket) => (
          <AssigneesCellContent
            entityId={entityId}
            ticket={ticket}
            onUpdate={produceUpdateTicketFn(ticket.id)}
          />
        ),
      },
      {
        id: "priority",
        title: "Priority",
        width: 200,
        resizeOptions: { isEnabled: true },
        className: "min-h-[79px]",
        headerClassName: "px-4 h-10",
        content: (ticket) => (
          <PriorityCellContent
            ticket={ticket}
            onUpdate={produceUpdateTicketFn(ticket.id)}
          />
        ),
      },
      {
        id: "due-date",
        title: "Due Date",
        width: 200,
        resizeOptions: { isEnabled: true },
        className: "min-h-[79px]",
        headerClassName: "px-4 h-10",
        content: (ticket) => (
          <DueDateCellContent
            ticket={ticket}
            onUpdate={produceUpdateTicketFn(ticket.id)}
          />
        ),
      },
      {
        id: "created-at",
        title: "Created at",
        width: 200,
        grow: true,
        // We don't allow resizing of the last column in the table
        resizeOptions: { isEnabled: false },
        className: "min-h-[79px]",
        headerClassName: "px-4 h-10",
        content: (ticket) => <CreatedAtCellContent ticket={ticket} />,
      },
    ],
    [entityId, produceUpdateTicketFn]
  );

  return columns;
};
